import { fetchQuranJson } from './dataSource';

/** One entry of Quran.com's recitations list. */
export interface Reciter {
  id: number;
  reciter_name?: string;
  style?: string;
}

export const RECITERS_URL = 'https://api.quran.com/api/v4/resources/recitations?language=en';

/** al-Minshāwī murattal, else any Minshāwī, else the first listed, else Quran.com id 9 (legacy loadReciters). */
export function chooseDefaultReciter(reciters: Reciter[]): number {
  const minshawi = (r: Reciter) => /minshawi/i.test(r.reciter_name ?? '');
  const pick = reciters.find((r) => minshawi(r) && /murattal/i.test(r.style ?? ''))
    ?? reciters.find(minshawi)
    ?? reciters[0];
  return pick ? pick.id : 9;
}

export function verseAudioUrlEndpoint(reciterId: number, verseKey: string): string {
  return `https://api.quran.com/api/v4/recitations/${reciterId}/by_ayah/${verseKey}`;
}

export function wordTimingsEndpoint(reciterId: number, verseKey: string): string {
  return 'https://api.quran.com/api/v4/verses/by_key/' + verseKey + '?audio=' + reciterId;
}

/** Quran.com returns verse audio as a path or a protocol-relative URL (legacy verseAudioUrl). */
export function normaliseVerseAudioUrl(url: string | null | undefined): string {
  const u = url ?? '';
  if (!u) return '';
  if (u.startsWith('//')) return 'https:' + u;
  if (!/^https?:/.test(u)) return 'https://verses.quran.com/' + u.replace(/^\/+/, '');
  return u;
}

/** A word timing: [segment index, Quran.com word position, start ms, end ms]. */
export type WordSegment = [number, number, number, number];

/** The Quran.com word position being recited at `ms` into the verse, or null between words. */
export function positionAtTime(segments: WordSegment[], ms: number): number | null {
  const hit = segments.find((s) => ms >= s[2] && ms < s[3]);
  return hit ? hit[1] : null;
}

interface VerseAudioPayload {
  audio_files?: { url?: string | null }[];
}

interface WordTimingsPayload {
  verse?: { audio?: { segments?: WordSegment[] } | null };
}

type LoadJson = <T>(url: string) => Promise<T>;

/**
 * Verse audio URLs and per-word timings for the chosen reciter, cached per
 * reciter and verse for the life of the page (legacy REAL_AUDIO.urls plus
 * My Khatam's SEG_CACHE). A failed fetch is not cached, so it is retried.
 */
export class RecitationSource {
  private readonly urls = new Map<string, Promise<string>>();
  private readonly timings = new Map<string, Promise<WordSegment[]>>();

  constructor(private readonly load: LoadJson = fetchQuranJson) {}

  verseAudioUrl(reciterId: number, verseKey: string): Promise<string> {
    const key = reciterId + '|' + verseKey;
    let promise = this.urls.get(key);
    if (!promise) {
      promise = this.load<VerseAudioPayload>(verseAudioUrlEndpoint(reciterId, verseKey))
        .then((d) => normaliseVerseAudioUrl(d.audio_files?.[0]?.url))
        .catch((error: unknown) => { this.urls.delete(key); throw error; });
      this.urls.set(key, promise);
    }
    return promise;
  }

  /** Empty when the reciter has no word timings for the verse (or the fetch fails). */
  wordTimings(reciterId: number, verseKey: string): Promise<WordSegment[]> {
    const key = reciterId + '|' + verseKey;
    let promise = this.timings.get(key);
    if (!promise) {
      promise = this.load<WordTimingsPayload>(wordTimingsEndpoint(reciterId, verseKey))
        .then((d) => d.verse?.audio?.segments ?? [])
        .catch(() => { this.timings.delete(key); return []; });
      this.timings.set(key, promise);
    }
    return promise;
  }
}
